//Get action props from the selection state generated by the Workspace.
import { useContext } from 'react';
import WorkspaceContext from '../components/Context/WorkspaceContext';
import getClientMutationId from './getClientMutationId';
import normalizeVariables, { normalizeFields } from '../graphql/schema/normalizeVariables';
import { IFilterInput } from '../graphql/schema/p4types';
import { IWorkspaceSelection, IWorkspaceSelectionState } from '../components/Workspace/useWorkspaceSelection';

export type IActionProps<ActionEntities = { [key: string]: Array<any> }> = ActionEntities & {
  filters: Array<IFilterInput>;
  include: boolean;
};

/**
 * Get action criteria for workspace bulk actions, using a specified name for the action entities list.
 * @param type - Action entities list name in the resolver.
 * @deprecated - Recommended to use ActionCriteria<> and the below useActionCriteria hook to generate it, when possible.
 */
export const useActionProps: <ActionEntities>(type: string) => IActionProps<ActionEntities> | null = (type) => {
  const { filters, actionEntities, include } = useActionCriteria();

  return {
    filters: filters,
    [type]: actionEntities,
    include: include,
  } as IActionProps<any>;
};

/**
 * As above, but specifying the type with a specific name of 'actionEntities' (Standardized name for 'ActionCriteria' DTO).
 */
export const useActionCriteria = (): {
  filters: Array<IFilterInput>;
  actionEntities: Array<any>;
  include: boolean;
} | null => {
  const workspace = useContext(WorkspaceContext);

  const { selectionState, workspaceSelection } = workspace;
  const { actionEntities, selectedAll, filters } =
    selectionState ??
    ({
      actionEntities: [],
      selectedAll: false,
      filters: [],
      path: '',
      count: 0,
    } as IWorkspaceSelectionState);
  const { orderBy, orderDirection } = workspaceSelection ?? ({} as IWorkspaceSelection);

  return normalizeVariables({
    filters,
    actionEntities,
    include: !selectedAll,
    orderBy,
    orderDirection,
  });
};

/**
 * Small enhancement to useActionProps to wrap as input with clientMutationId - Commonly used for mutation calls.
 *
 * @deprecated - Preferred to use 'useActionCriteria' directly and provide other variables as required by the query types.
 *             - (With the endpoint directly taking ActionCriteria<Entity> rather than the constituent parts).
 */
export const useActionVariables: (
  type: string,
  additionalInputVariables?: any,
  additionalNormalizeFields?: Array<string>
) => { input: { clientMutationId: string; filters: Array<IFilterInput>; include: boolean; [key: string]: any } } = (
  type,
  additionalInputVariables = {},
  additionalNormalizeFields = []
) => {
  const actionProps: any = useActionProps(type);

  return {
    input: {
      ...normalizeVariables(actionProps, [...normalizeFields, ...additionalNormalizeFields]),
      ...additionalInputVariables,
      clientMutationId: getClientMutationId(),
    },
  };
};
